import Zod, { z } from "zod";

import {
  normalizeStateName,
  STATE_NORMALIZED_NAME_TO_ABBREVIATION,
  StateAbbreviation,
  STATES,
  STATES_BY_ABBREVIATION,
} from "@stw/states";

// Must match the Prisma AudienceContactFieldType enum
export const AudienceContactFieldTypeSchema = z.enum([
  "STRING",
  "BOOLEAN",
  "NUMBER",
  "ENUM",
  "STATE",
  "ZIP",
]);
export type AudienceContactFieldType = z.infer<
  typeof AudienceContactFieldTypeSchema
>;

export type AudienceContactField = {
  id: string;
  name: string;
  type: AudienceContactFieldType;
  typeParameters: Record<string, any>;
};

export type AudienceContactFieldTypeProperties<
  Value,
  TypeParameters,
  Input = Value,
> = {
  typeParameterSchema: Zod.Schema<TypeParameters>;
  valueSchema: (
    typeParameters: TypeParameters
  ) =>
    | Zod.ZodType<Value, Zod.ZodTypeDef, Input>
    | Zod.ZodDefault<
        Zod.ZodEffects<
          Zod.ZodNullable<Zod.ZodType<Value, Zod.ZodTypeDef, Input>>,
          Value,
          Value
        >
      >;
  getSearchTerms(value: Value | null, typeParameters: TypeParameters): string[];
};

type NoTypeParameters = Record<string, never>;

const TypeParameterSchema = {
  STRING: z.object({ maxLength: z.coerce.number().min(1) }),
  BOOLEAN: z.object({}),
  NUMBER: z.object({}),
  ENUM: z.object({ values: z.array(z.string().min(1)) }),
  STATE: z.object({}),
  ZIP: z.object({}),
};

export const AudienceContactTypeTypeDefinitions = {
  STRING: {
    typeParameterSchema: TypeParameterSchema.STRING.strict(),
    valueSchema: params =>
      z
        .string()
        .max(params.maxLength)
        .nullable()
        .transform((value: string | null) => {
          if (!value) {
            return null;
          }

          const trimmed = value.trim();

          if (trimmed.length === 0) {
            return null;
          }

          return trimmed;
        })
        .default(null),
    getSearchTerms: val => (val ? [val] : []),
  } satisfies AudienceContactFieldTypeProperties<
    string | null,
    { maxLength: number }
  >,
  BOOLEAN: {
    typeParameterSchema: TypeParameterSchema.BOOLEAN.strict(),
    valueSchema: () =>
      z.preprocess(v => {
        const truthyValues = ["true", "1", "yes"];
        const falsyValues = ["false", "0", "no"];
        const nullValues = ["null", ""];
        if (typeof v === "string") {
          const trimmed = v.trim().toLowerCase();
          if (truthyValues.includes(trimmed)) {
            return true;
          }
          if (falsyValues.includes(trimmed)) {
            return false;
          }
          if (nullValues.includes(trimmed)) {
            return null;
          }
        } else {
          return v != undefined ? v : null;
        }
        return v;
      }, z.boolean().nullable()) as Zod.ZodType<
        boolean | null,
        Zod.ZodTypeDef,
        boolean | null
      >,
    getSearchTerms: () => [],
  } satisfies AudienceContactFieldTypeProperties<
    boolean | null,
    NoTypeParameters
  >,
  NUMBER: {
    typeParameterSchema: TypeParameterSchema.NUMBER.strict(),
    valueSchema: () =>
      z.coerce.number({ message: "Must be a number" }).nullable(),
    getSearchTerms: val => (val === null ? [] : [String(val)]),
  } satisfies AudienceContactFieldTypeProperties<
    number | null,
    NoTypeParameters
  >,
  ENUM: {
    typeParameterSchema: TypeParameterSchema.ENUM.strict(),
    getSearchTerms: value => (value ? [value] : []),
    valueSchema: (params: { values: string[] }) =>
      z
        .string()
        .nullable()
        .transform(v => v || null)
        .refine(value => value === null || params.values.includes(value), {
          message: "Value must be one of the defined enum values.",
        }),
  } satisfies AudienceContactFieldTypeProperties<
    string | null,
    { values: string[] }
  >,
  STATE: {
    typeParameterSchema: TypeParameterSchema.STATE.strict(),
    valueSchema: () =>
      z
        .string()
        .nullable()
        .transform((value, ctx) => {
          if (value) {
            const normalized = normalizeStateName(value);
            if (STATE_NORMALIZED_NAME_TO_ABBREVIATION[normalized]) {
              return STATE_NORMALIZED_NAME_TO_ABBREVIATION[normalized];
            }

            const upper = normalized.toUpperCase();
            if (upper in STATES_BY_ABBREVIATION) {
              return upper;
            }

            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Must be a state name or abbreviation",
              fatal: true,
            });

            return z.NEVER;
          }

          return null;
        })
        .pipe(
          z
            .enum(
              STATES.map(state => state.abbreviation) as [
                StateAbbreviation,
                ...StateAbbreviation[],
              ]
            )
            .nullable()
        ),
    getSearchTerms: value => {
      if (value === null) {
        return [];
      }
      const state = STATES_BY_ABBREVIATION[value as StateAbbreviation];
      if (!state) {
        return [];
      }

      return [state.abbreviation, state.name, ...(state.alternateNames || [])];
    },
  } satisfies AudienceContactFieldTypeProperties<
    StateAbbreviation | null,
    NoTypeParameters,
    string | null
  >,
  ZIP: {
    typeParameterSchema: TypeParameterSchema.ZIP.strict(),
    valueSchema: () =>
      z
        .string()
        .nullable()
        .transform((value, ctx) => {
          if (value) {
            const normalized = value.trim();
            if (normalized.match(/^\d{5}$/)) {
              return normalized;
            }

            if (normalized.match(/^\d{5}-?\d{4}$/)) {
              return normalized.substring(0, 5);
            }

            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Must be a 5-digit or 9-digit zip code",
              fatal: true,
            });

            return z.NEVER;
          }

          return null;
        }),
    getSearchTerms: value => (value ? [value] : []),
  } satisfies AudienceContactFieldTypeProperties<
    string | null,
    NoTypeParameters
  >,
} as const;

/**
 * Builds a Zod schema for a set of audience contact fields. Does *not* validate
 * the type parameters of the fields.
 */
export function buildZodSchemaForAudienceContactFields(
  fields: AudienceContactField[],
  format: "id" | "name" = "id"
): Zod.ZodObject<Record<string, Zod.ZodTypeAny>> {
  return z.object(
    Object.fromEntries(
      fields.map(field => {
        const typeDefinition = AudienceContactTypeTypeDefinitions[field.type];
        if (!typeDefinition) {
          throw new Error(`Unknown audience contact field type: ${field.type}`);
        }

        return [
          format === "id" ? field.id : field.name,
          typeDefinition.valueSchema(field.typeParameters as any).optional(),
        ];
      })
    )
  );
}

export function parseFieldValue(
  field: AudienceContactField,
  value: unknown
): string | number | boolean | null {
  const typeDefinition = AudienceContactTypeTypeDefinitions[field.type];
  if (!typeDefinition) {
    throw new Error(`Unknown audience contact field type: ${field.type}`);
  }

  return typeDefinition.valueSchema(field.typeParameters as any).parse(value);
}

export function buildZodSchemaForAcfColumnSelect(
  fields: AudienceContactField[],
  format: "id" | "name" = "id"
): Zod.ZodObject<Record<string, Zod.ZodTypeAny>> {
  return z.object(
    Object.fromEntries(
      fields.map(field => [
        format === "id" ? field.id : field.name,
        z.string().optional(),
      ])
    )
  );
}

export function validateAudienceContactField(
  type: AudienceContactFieldType,
  typeParameters: unknown
): void {
  const typeDefinition = AudienceContactTypeTypeDefinitions[type];
  if (!typeDefinition) {
    throw new Error(`Unknown audience contact field type: ${type}`);
  }

  typeDefinition.typeParameterSchema.parse(typeParameters);
}

export function getAudienceContactFieldSearchTerms(
  type: AudienceContactFieldType,
  value: unknown
): string[] {
  const typeDefinition = AudienceContactTypeTypeDefinitions[type];
  if (!typeDefinition) {
    throw new Error(`Unknown audience contact field type: ${type}`);
  }

  return typeDefinition.getSearchTerms(value as any);
}

const CreateContactFieldNameSchema = z
  .string()
  .min(1, "Field name is required")
  .max(50, "Field name must be 50 characters or less")
  .transform(name => name.trim())
  .refine(name => name.length > 0, "Field name cannot be empty or only spaces");

export const EnumSchema = z.object({
  type: z.literal("ENUM"),
  name: CreateContactFieldNameSchema,
  typeParameters: z.object({
    values: z.array(z.object({ value: z.string().min(1) })).min(1),
  }),
});

export const EnumInputSchema = z.object({
  type: z.literal("ENUM"),
  name: CreateContactFieldNameSchema,
  typeParameters: z.object({
    values: z.array(z.string().min(1)).min(1),
  }),
});

const CreateContactFieldBaseSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("STRING"),
    name: CreateContactFieldNameSchema,
    typeParameters: TypeParameterSchema.STRING,
  }),
  z.object({
    type: z.literal("BOOLEAN"),
    name: CreateContactFieldNameSchema,
    typeParameters: TypeParameterSchema.BOOLEAN,
  }),
  z.object({
    type: z.literal("NUMBER"),
    name: CreateContactFieldNameSchema,
    typeParameters: TypeParameterSchema.NUMBER,
  }),
  EnumSchema,
  z.object({
    type: z.literal("STATE"),
    name: CreateContactFieldNameSchema,
    typeParameters: TypeParameterSchema.STATE,
  }),
  z.object({
    type: z.literal("ZIP"),
    name: CreateContactFieldNameSchema,
    typeParameters: TypeParameterSchema.ZIP,
  }),
]);

const checkForDuplicateEnumValues = <
  T extends { type: string; typeParameters?: unknown },
>(
  data: T,
  ctx: Zod.RefinementCtx
) => {
  if (
    data.type === "ENUM" &&
    Array.isArray((data.typeParameters as { values: unknown }).values)
  ) {
    const valuesArray = (
      data.typeParameters as { values: (string | { value: string })[] }
    ).values;

    // Handle both string[] format and {value: string}[] format
    const isStringArray = valuesArray.every(item => typeof item === "string");
    const values = valuesArray.map(item =>
      typeof item === "string" ? item : item.value
    );

    const lowerCaseValues = values.map((value: string) => value.toLowerCase());
    const uniqueValues = new Set(lowerCaseValues);

    if (uniqueValues.size !== lowerCaseValues.length) {
      const seen = new Set<string>();
      values.forEach((value: string, index: number) => {
        const lowerValue = value.toLowerCase();
        if (seen.has(lowerValue)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate option value: '${value}'`,
            path: [
              "typeParameters",
              "values",
              index,
              ...(isStringArray ? [] : ["value"]),
            ],
          });
        }
        seen.add(lowerValue);
      });
    }
  }
};

export const CreateContactFieldUISchema =
  CreateContactFieldBaseSchema.superRefine((arg, ctx) =>
    checkForDuplicateEnumValues(arg, ctx)
  );

export const createContactFieldUISchemaWithDuplicateValidation = (
  existingFieldNames: string[] = []
): ReturnType<typeof CreateContactFieldUISchema.superRefine> => {
  return CreateContactFieldUISchema.superRefine((data, ctx) => {
    const normalizedName = data.name.toLowerCase().trim();
    const isDuplicate = existingFieldNames
      .map(name => name.toLowerCase().trim())
      .includes(normalizedName);

    if (isDuplicate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A field with this name already exists",
        path: ["name"],
      });
    }
  });
};

export const CreateContactFieldSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("STRING"),
      name: CreateContactFieldNameSchema,
      typeParameters: TypeParameterSchema.STRING.strict(),
    }),
    z.object({
      type: z.literal("BOOLEAN"),
      name: CreateContactFieldNameSchema,
      typeParameters: TypeParameterSchema.BOOLEAN.strict(),
    }),
    z.object({
      type: z.literal("NUMBER"),
      name: CreateContactFieldNameSchema,
      typeParameters: TypeParameterSchema.NUMBER.strict(),
    }),
    z.object({
      type: z.literal("ENUM"),
      name: CreateContactFieldNameSchema,
      typeParameters: TypeParameterSchema.ENUM.strict(),
    }),
    z.object({
      type: z.literal("STATE"),
      name: CreateContactFieldNameSchema,
      typeParameters: TypeParameterSchema.STATE.strict(),
    }),
    z.object({
      type: z.literal("ZIP"),
      name: CreateContactFieldNameSchema,
      typeParameters: TypeParameterSchema.ZIP.strict(),
    }),
  ])
  .superRefine((arg, ctx) => checkForDuplicateEnumValues(arg, ctx));
